import {Quaternion, Vector3, type PerspectiveCamera, type Scene, type WebGLRenderer} from 'three'

import {CAPTURE_JPEG_QUALITY, CAPTURE_MAX_DIMENSION} from '../config'
import {AppError} from '../util/errors'

/**
 * The containment wall around the untyped vendor global.
 *
 * This is the ONLY file allowed to touch `XR8`. Everything downstream talks to
 * `Xr8Facade`, which means:
 *
 * - 8th Wall's plain `{x, y, z}` bags become `THREE.Vector3` exactly once, so
 *   no other file ever has to handle both shapes.
 * - Placement, gestures and the UI state machine are unit-testable against a
 *   fake facade with no browser and no camera.
 * - If the engine is ever replaced (WebXR on iOS, a fork after the sunset, a
 *   commercial alternative), one file changes.
 */

export type HitKind = Xr8HitTestResult['type']

export interface PlacementHit {
  readonly position: Vector3
  readonly distance: number
  readonly kind: HitKind
}

export interface Xr8Facade {
  readonly scene: Scene
  readonly camera: PerspectiveCamera
  readonly renderer: WebGLRenderer
  /**
   * @param x01 0..1 from the LEFT edge of the camera feed
   * @param y01 0..1 from the TOP edge of the camera feed
   */
  hitTest(x01: number, y01: number, kinds: readonly HitKind[]): readonly PlacementHit[]
  /** Resets the tracking origin. Use after tracking is lost and recovered. */
  recenter(): void
  /** Tells the engine where the camera is so it can build the projection. */
  syncCameraOrigin(): void
  /**
   * Reads the composited canvas back as base64 JPEG bytes, `data:` prefix
   * already stripped. Resolves on a later frame than the call, never the
   * current one.
   *
   * Deliberately a 1:1 wrapper that stops at the string the engine returns.
   * Decoding it into a `Blob`/`File` has nothing to do with XR8, so it lives in
   * `src/capture/photo.ts` where it stays a pure function - testable with no
   * camera, no canvas and no vendor global in sight.
   *
   * If a campaign ever needs a logo or hashtag burnt into the photo, the ONLY
   * insertion point is `takeScreenshot({onProcessFrame: ({ctx}) => …})` below:
   * the engine hands over the 2D context after the frame is drawn and before it
   * is encoded. Nothing is wired for it today.
   */
  captureJpegBase64(): Promise<string>
}

/**
 * Must be called from inside a pipeline module's `onStart` or later -
 * `XR8.Threejs.xrScene()` is not valid before the Threejs module has run.
 */
export const createXr8Facade = (xr8: Xr8Global): Xr8Facade => {
  const {scene, camera, renderer} = xr8.Threejs.xrScene()

  // Once, here, rather than per capture: these are constants, and the engine
  // stores them on a module-level singleton. `maxDimension` only scales down, so
  // it acts as a ceiling on the camera's native resolution, not a target.
  xr8.CanvasScreenshot.configure({
    maxDimension: CAPTURE_MAX_DIMENSION,
    jpgCompression: CAPTURE_JPEG_QUALITY,
  })

  return {
    scene,
    camera,
    renderer,

    hitTest: (x01, y01, kinds) =>
      xr8.XrController.hitTest(x01, y01, kinds).map((hit) => ({
        position: new Vector3(hit.position.x, hit.position.y, hit.position.z),
        distance: hit.distance,
        kind: hit.type,
        // hit.rotation is deliberately dropped. Feature-point rotations are
        // noisy and produce visibly tilted models; callers derive an upright
        // yaw from the camera position instead.
      })),

    recenter: () => xr8.XrController.recenter(),

    // The engine rejects with a bare Error ("Unable to read pixels from
    // canvas.", "Not attached to a running camera feed"). Wrapping it keeps the
    // rule from util/errors.ts intact: every failure a user can hit has a code
    // and a Japanese message, and neither of those strings is either.
    captureJpegBase64: () =>
      xr8.CanvasScreenshot.takeScreenshot().catch((cause: unknown) => {
        throw new AppError('CAPTURE_FAILED', undefined, {cause})
      }),

    syncCameraOrigin: () => {
      const origin = new Vector3()
      const facing = new Quaternion()
      camera.getWorldPosition(origin)
      camera.getWorldQuaternion(facing)
      xr8.XrController.updateCameraProjectionMatrix({
        origin: {x: origin.x, y: origin.y, z: origin.z},
        facing: {x: facing.x, y: facing.y, z: facing.z, w: facing.w},
      })
    },
  }
}
